import { Component, EventEmitter, Inject, OnInit, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ContentService, Content, ContentMedia, ContentFaq } from '@app/_components/content';
import { ContentRevision } from '@app/_components/content/content';
import { AlertService } from '@app/_services';
import { MediaUploadService } from '@app/_services/media-upload.service';
import { MessageService } from 'primeng/api';
import { DevelopmentService } from '@app/_components/development/development.service';
import { PropertyService } from '@app/_components/property/property.service';
import { PictureService } from '@app/_components/picture/picture.service';
import { first } from 'rxjs/operators';

@Component({
    selector: 'app-content-form',
    templateUrl: './content-form.component.html',
    styleUrls: ['./content-form.component.css'],
    providers: [MessageService],
    standalone: false
})
export class ContentFormComponent implements OnInit, OnDestroy {
    @Output() doAdd = new EventEmitter<any>();
    @Output() doUpdate = new EventEmitter<any>();
    @Output() doDelete = new EventEmitter<any>();

    content: Content;
    form1: FormGroup;
    formTitle: string;
    formLoading = false;
    isNew = true;
    // Guard that delays TinyMCE mount until after full content is loaded so that
    // the editor never fires onChange("") over the top of a patchValue'd bodyHtml.
    contentLoaded = false;
    mediaList: ContentMedia[] = [];
    faqList: ContentFaq[] = [];
    faqDragOverIndex: number | null = null;
    uploadingDialog = false;
    uploadingCount = 0;

    statuses: any;
    contentTypes: any;
    developments: any[] = [];
    locations: any[] = [];
    properties: any[] = [];
    adrRegions: any[] = [];

    tinyMceInit: any;

    // Feature #10: Revision History
    showRevisions = false;
    revisions: ContentRevision[] = [];
    revisionsLoading = false;

    // Feature #12: Entity Picker
    showEntityPicker = false;
    entityPickerTabIndex = 0;
    entityPickerGuides: any[] = [];
    entityPickerFaqs: any[] = [];
    entityPickerSearch = '';
    entityPickerLoading = false;
    private tinyMceEditorRef: any = null;

    // Feature #14: Workflow
    showWorkflowComment = false;
    workflowTargetStatus = '';
    workflowComment = '';
    workflowAssignedUserId: number = null;
    workflowLoading = false;

    // Feature #17: Autolink Suggestions
    autolinkSuggestions: any[] = [];
    showAutolinkPanel = false;
    autolinkLoading = false;

    constructor(
        private formBuilder: FormBuilder,
        private contentService: ContentService,
        private alertService: AlertService,
        private messageService: MessageService,
        public mediaUploadService: MediaUploadService,
        private developmentService: DevelopmentService,
        private propertyService: PropertyService,
        private pictureService: PictureService,
        @Inject(MAT_DIALOG_DATA) data: any
    ) {
        this.formTitle = data.title;
        this.content = data?.content;
        this.statuses = contentService.statuses;
    }

    ngOnInit() {
        this.formBuild();
        this.wireStatementMonthValidation();
        this.buildTinyMceInit();
        this.loadDevelopments();
        this.loadContentTypes();
        this.loadLocations();
        this.loadProperties();
        if (this.content) {
            this.isNew = false;
            // The list view uses a summary projection that omits bodyHtml and other
            // large text fields. Always reload the full record before patching the form
            // so that TinyMCE is initialised with the real body content rather than null,
            // which would otherwise overwrite the existing body with an empty string on save.
            // contentLoaded is set to true AFTER patchValue so that the TinyMCE <editor>
            // is not mounted until bodyHtml is already in the form control — this prevents
            // TinyMCE's async init from firing onChange("") over the top of the real content.
            this.contentService.findById(this.content.contentId).pipe(first()).subscribe({
                next: (res: any) => {
                    const full = res?.data?.content;
                    if (full) {
                        this.content = full;
                    }
                    this.form1.patchValue(this.content);
                    // Reconcile contentTypeId in case loadContentTypes() already
                    // completed and its spurious SelectControlValueAccessor event
                    // mutated the form value before patchValue ran (or after it ran
                    // but with an empty option list). This ensures statementMonth
                    // validators are always in sync with the server-authoritative type.
                    this.reconcileContentTypeId();
                    this.mediaList = this.content.mediaList ? [...this.content.mediaList] : [];
                    this.faqList = this.content.faqList ? [...this.content.faqList] : [];
                    this.contentLoaded = true;
                    this.loadAutolinkSuggestions();
                },
                error: () => {
                    // Fallback: patch with whatever the list already provided
                    this.form1.patchValue(this.content);
                    this.mediaList = this.content.mediaList ? [...this.content.mediaList] : [];
                    this.faqList = this.content.faqList ? [...this.content.faqList] : [];
                    this.contentLoaded = true;
                }
            });
        } else {
            // New record — no content to load, show the editor immediately
            this.contentLoaded = true;
        }
        this.mediaUploadService.clearQueue();
    }

    loadContentTypes(): void {
        this.contentService.findAllContentTypes().pipe(first()).subscribe({
            next: (res: any) => {
                this.contentTypes = res?.data?.contentTypes || [];
                // After content-type options load, Angular's SelectControlValueAccessor
                // can fire a spurious valueChanges event that changes the form control
                // value from the real contentTypeId (e.g. 3=Guide) to the first available
                // option (2=Statement Letter) if the options were not yet present when
                // patchValue ran.
                //
                // The spurious event fires during change detection AFTER this callback
                // returns, so we defer reconcileContentTypeId() by one macro-task tick
                // (setTimeout 0) to ensure it runs AFTER the spurious event has mutated
                // the form value. This guarantees the authoritative value is always restored.
                setTimeout(() => this.reconcileContentTypeId(), 0);
            },
            error: () => {}
        });
    }

    /**
     * Restores the form's contentTypeId to the server-authoritative value (this.content)
     * whenever async events may have mutated it, and re-syncs statementMonth validators.
     *
     * Called in two places to handle the race between loadContentTypes() and findById():
     *   1. In the findById() next callback after patchValue, so that a correct value is
     *      guaranteed once the full content is loaded.
     *   2. In loadContentTypes() next callback, so that if options loaded AFTER patchValue
     *      and a spurious SelectControlValueAccessor event changed the form value, we
     *      restore it back to the server-authoritative value immediately.
     *
     * For new records (isNew=true), this.content is null and no restoration occurs; the
     * form value set by the user (or default=3) is respected.
     */
    private reconcileContentTypeId(): void {
        if (!this.isNew && this.content?.contentTypeId != null) {
            const authoritative = Number(this.content.contentTypeId);
            const current = Number(this.form1.get('contentTypeId')?.value);
            if (current !== authoritative) {
                // Spurious SelectControlValueAccessor event mutated the form value.
                // Restore the authoritative value without triggering valueChanges
                // (emitEvent:false prevents re-running the validator subscription).
                this.form1.patchValue({ contentTypeId: authoritative }, { emitEvent: false });
            }
            this.applyStatementMonthValidation(authoritative);
        } else {
            // For new records, always re-sync validators to whatever the user selected.
            this.applyStatementMonthValidation(this.form1.get('contentTypeId')?.value);
        }
    }

    loadLocations(): void {
        this.pictureService.getLocations().pipe(first()).subscribe({
            next: (res: any) => { this.locations = res?.data?.locations || res?.data?.locationList || []; },
            error: () => {}
        });
    }

    loadProperties(): void {
        this.propertyService.findAllSummaryWithStatusActive().pipe(first()).subscribe({
            next: (res: any) => { this.properties = res?.data?.shortTermRentalSummary || res?.data?.shortTermRentalList || []; },
            error: () => {}
        });
    }

    loadDevelopments(): void {
        this.developmentService.developmentSummaryList().pipe(first()).subscribe(
            (res: any) => {
                this.developments = (res.data.developmentSummaryList || []).map((d: any) => ({
                    ...d,
                    displayName: d.adrCounty ? `${d.addressDevelopmentName} (${d.adrCounty})` : d.addressDevelopmentName
                }));
                const regions = new Set<string>();
                this.developments.forEach((d: any) => { if (d.adrRegion) regions.add(d.adrRegion); });
                this.adrRegions = Array.from(regions).sort().map(r => ({ label: r, value: r }));
            },
            () => {}
        );
    }

    ngOnDestroy(): void {
        this.mediaUploadService.clearQueue();
    }

    buildTinyMceInit(): void {
        this.tinyMceInit = {
            height: 500,
            base_url: '/tinymce',
            suffix: '.min',
            plugins: 'code anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount',
            toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat | code | insertentitylink',
            // Mirror the inline CSS applied by inlineNewsletterCss() in CommonMsgBL.java so
            // the editor preview matches what owners actually receive in emailed statements.
            content_style: 'p { margin: 0 0 8px 0; padding: 0; } ul, ol { margin: 0 0 8px 0; padding-left: 20px; } li { margin-bottom: 4px; }',
            automatic_uploads: true,
            paste_data_images: true,
            setup: (editor: any) => {
                this.tinyMceEditorRef = editor;
                editor.ui.registry.addButton('insertentitylink', {
                    text: 'Insert Link',
                    icon: 'link',
                    tooltip: 'Insert link to a Guide or FAQ',
                    onAction: () => {
                        this.openEntityPicker(editor);
                    }
                });
            },
            images_upload_handler: (blobInfo: any, progress: (pct: number) => void) => {
                return new Promise<string>((resolve, reject) => {
                    const file = new File([blobInfo.blob()], blobInfo.filename(), { type: blobInfo.blob().type });
                    this.mediaUploadService.upload(file).subscribe({
                        next: (item) => {
                            if (item.progressPct) progress(item.progressPct);
                            if (item.status === 'done' && item.remoteUrl) resolve(item.remoteUrl);
                            if (item.status === 'error') reject(item.error);
                        }
                    });
                });
            }
        };
    }

    /**
     * Layer 2 frontend guard added 2026-05-12 P0 follow-up. When the user
     * selects Content Type = Statement Letter (contentTypeId = 2), the
     * statementMonth field becomes mandatory and must match MM/yyyy format
     * with a zero-padded month (e.g. "04/2026"). When the type is anything
     * else, all validators on statementMonth are cleared.
     *
     * Background — three production statement-letter rows (1631 Oahu, 1634
     * Big Island, 1637 Park City) were saved with statementMonth = NULL,
     * which caused the email-compose flow to surface "Value not present
     * for: {{ownerStmt.blogStatementCurrentMonth}}" because the lookup
     * filter never matched. Layer 1 (backend) rejects the save; this
     * Layer 2 guard surfaces the error to the user inline before they hit
     * the submit button.
     *
     * SPURIOUS valueChanges GUARD (2026-05-20): Angular's
     * SelectControlValueAccessor can fire a spurious valueChanges event
     * with the first available option value (Statement Letter, id=2) while
     * reconciling the option list after loadContentTypes() resolves.  To
     * prevent stale validators from blocking Guide/Blog saves, callers
     * must call applyStatementMonthValidation() at submission time to
     * re-sync validators with the actual form value.
     */
    private readonly STATEMENT_TYPE_ID = 2;
    private readonly MONTH_REGEX = /^(0[1-9]|1[0-2])\/[0-9]{4}$/;

    private applyStatementMonthValidation(typeId: any): void {
        const ctrl = this.form1.get('statementMonth');
        if (!ctrl) return;
        if (Number(typeId) === this.STATEMENT_TYPE_ID) {
            ctrl.setValidators([Validators.required, Validators.pattern(this.MONTH_REGEX)]);
        } else {
            ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    private wireStatementMonthValidation(): void {
        // initial pass — picks up the form's current contentTypeId value
        this.applyStatementMonthValidation(this.form1.get('contentTypeId')?.value);
        // and react to every subsequent USER-driven change
        this.form1.get('contentTypeId')?.valueChanges.subscribe(v => this.applyStatementMonthValidation(v));
    }

    formBuild() {
        this.form1 = this.formBuilder.group({
            contentId: [],
            contentTypeId: [3, Validators.required],
            category: [],
            statementMonth: [],
            title: [null, [Validators.required, Validators.minLength(3)]],
            slug: [],
            bodyHtml: [],
            bodyText: [],
            summary: [],
            metaTitle: [],
            metaDescription: [],
            keywords: [],
            schemaType: [],
            status: ['DRAFT', Validators.required],
            locationId: [],
            shortTermRentalId: [],
            developmentId: [],
            developmentIds: [[]],
            adrRegion: [],
            sortOrder: [],
            generatedBy: [],
            enabled: [true],
            featured: [false],
            scheduledPublishDate: [null],
        });
    }

    formAdd() {
        // Re-sync statementMonth validators with the actual contentTypeId at
        // submission time.  A spurious SelectControlValueAccessor valueChanges
        // event fired during async option-list reconciliation can leave stale
        // validators on statementMonth (e.g. required/pattern on a Guide save),
        // making the form permanently invalid even when all user-visible fields
        // are filled.  Calling applyStatementMonthValidation() here ensures the
        // validator state always reflects the form's real contentTypeId.
        this.applyStatementMonthValidation(this.form1.get('contentTypeId')?.value);
        this.form1.markAllAsTouched();
        if (this.form1.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill in all required fields (Title, Content Type, Status).', life: 5000 });
            return;
        }
        if (this.mediaUploadService.hasActiveUploads()) {
            this.showUploadingDialog(() => this.doFormAdd());
            return;
        }
        this.doFormAdd();
    }

    private doFormAdd(): void {
        this.formLoading = true;
        // Read TinyMCE content directly from the editor instance at save time to avoid
        // stale form-control values caused by TinyMCE's asynchronous initialisation.
        // During init, TinyMCE can fire onChange("") before it has finished loading the
        // queued value, leaving the reactive form control at "" while the editor visually
        // shows the real content.  getContent() always returns the live editor state.
        if (this.tinyMceEditorRef) {
            this.form1.patchValue({ bodyHtml: this.tinyMceEditorRef.getContent() }, { emitEvent: false });
        }
        const payload = { ...this.form1.value, mediaList: this.mediaList, faqList: this.faqList };
        this.contentService.add(payload).subscribe(
            (res: any) => {
                // Guard against null response (e.g. 401 interceptor swallows error and returns null)
                if (!res || !res.data?.content) {
                    this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Unexpected response from server. Please reload and try again.', life: 6000 });
                    this.formLoading = false;
                    return;
                }
                this.content = res.data.content;
                this.doAdd.emit(this.content);
            },
            (err: any) => {
                // Handle plain-string 504 error from error.interceptor (throwError(gatewayTimeoutMsg))
                // as well as structured Error objects. Do NOT reset formLoading here — keep the button
                // latched disabled after any error so the user cannot fire a duplicate POST without
                // reloading. This matches the bc82a7f5 pattern applied to reservation-create/access-code.
                const detail = typeof err === 'string' ? err : err?.error?.message || err?.message || 'An error occurred. Please try again.';
                this.messageService.add({ severity: 'error', summary: 'Save Failed', detail, life: 8000 });
            },
            () => { this.formLoading = false; }
        );
    }

    formUpdate() {
        // Same spurious-event guard as formAdd — see comment there.
        this.applyStatementMonthValidation(this.form1.get('contentTypeId')?.value);
        this.form1.markAllAsTouched();
        if (this.form1.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill in all required fields (Title, Content Type, Status).', life: 5000 });
            return;
        }
        if (this.mediaUploadService.hasActiveUploads()) {
            this.showUploadingDialog(() => this.doFormUpdate());
            return;
        }
        this.doFormUpdate();
    }

    private doFormUpdate(): void {
        this.formLoading = true;
        // Read TinyMCE content directly from the editor instance at save time — same
        // rationale as doFormAdd above.  This is the critical path for Sarah's bug:
        // editing an existing record's Body HTML field and clicking Update.
        if (this.tinyMceEditorRef) {
            this.form1.patchValue({ bodyHtml: this.tinyMceEditorRef.getContent() }, { emitEvent: false });
        }
        const payload = { ...this.form1.value, mediaList: this.mediaList, faqList: this.faqList };
        this.contentService.update(payload).subscribe(
            (res: any) => {
                // Guard against null response (e.g. 401 interceptor swallows error and returns null)
                if (!res || !res.data?.content) {
                    this.messageService.add({ severity: 'error', summary: 'Update Failed', detail: 'Unexpected response from server. Please reload and try again.', life: 6000 });
                    this.formLoading = false;
                    return;
                }
                this.content = res.data.content;
                this.doUpdate.emit(this.content);
            },
            (err: any) => {
                // Handle plain-string 504 error from error.interceptor (throwError(gatewayTimeoutMsg))
                // as well as structured Error objects. Do NOT reset formLoading here — keep the button
                // latched disabled after any error so the user cannot fire a duplicate PATCH without
                // reloading. This matches the bc82a7f5 pattern applied to reservation-create/access-code.
                const detail = typeof err === 'string' ? err : err?.error?.message || err?.message || 'An error occurred. Please try again.';
                this.messageService.add({ severity: 'error', summary: 'Update Failed', detail, life: 8000 });
            },
            () => { this.formLoading = false; }
        );
    }

    private showUploadingDialog(onComplete: () => void): void {
        this.uploadingDialog = true;
        const queue = this.mediaUploadService.getQueue();
        const sub = queue.subscribe(items => {
            this.uploadingCount = items.filter(i => i.status === 'pending' || i.status === 'uploading').length;
        });
        this.mediaUploadService.waitForAll().then(() => {
            sub.unsubscribe();
            this.uploadingDialog = false;
            onComplete();
        });
    }

    formDelete() {
        this.formLoading = true;
        this.contentService.delete(this.content.contentId).subscribe(
            (res: any) => {
                this.doDelete.emit(this.content);
            },
            (err: any) => {
                this.alertService.error(err);
                this.formLoading = false;
            },
            () => { this.formLoading = false; }
        );
    }

    faqAdd(): void {
        this.faqList = [...this.faqList, {
            contentFaqId: null,
            contentId: null,
            sectionName: null,
            questionDesc: '',
            answerDesc: '',
            imageUrl: null,
            displayOrder: this.faqList.length,
            enabled: true
        }];
    }

    faqRemove(index: number): void {
        const list = [...this.faqList];
        list.splice(index, 1);
        this.faqList = list;
    }

    faqImageSelect(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.uploadFaqImage(index, file);
        }
        input.value = '';
    }

    faqImageDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        this.faqDragOverIndex = index;
    }

    faqImageDragLeave(): void {
        this.faqDragOverIndex = null;
    }

    faqImageDrop(event: DragEvent, index: number): void {
        event.preventDefault();
        this.faqDragOverIndex = null;
        const file = event.dataTransfer?.files?.[0];
        if (file && file.type.startsWith('image/')) {
            this.uploadFaqImage(index, file);
        }
    }

    faqImagePaste(event: ClipboardEvent, index: number): void {
        const file = Array.from(event.clipboardData?.files || []).find(f => f.type.startsWith('image/'));
        if (file) {
            event.preventDefault();
            this.uploadFaqImage(index, file);
        }
    }

    faqImageClear(index: number): void {
        const list = [...this.faqList];
        list[index] = { ...list[index], imageUrl: null };
        this.faqList = list;
    }

    private uploadFaqImage(index: number, file: File): void {
        this.mediaUploadService.upload(file).subscribe(item => {
            if (item.status === 'done' && item.remoteUrl) {
                const list = [...this.faqList];
                list[index] = { ...list[index], imageUrl: item.remoteUrl };
                this.faqList = list;
            }
        });
    }

    // Feature #10: Revision History
    toggleRevisions(): void {
        if (!this.showRevisions && this.revisions.length === 0) {
            this.loadRevisions();
        }
        this.showRevisions = !this.showRevisions;
    }

    loadRevisions(): void {
        if (!this.content?.contentId) return;
        this.revisionsLoading = true;
        this.contentService.getRevisions(this.content.contentId).pipe(first()).subscribe(
            (res: any) => {
                this.revisions = res.data.revisions || [];
            },
            (err: any) => { this.alertService.error(err); },
            () => { this.revisionsLoading = false; }
        );
    }

    revertToRevision(revisionId: number): void {
        this.revisionsLoading = true;
        this.contentService.revertToRevision(this.content.contentId, revisionId).pipe(first()).subscribe(
            (res: any) => {
                const updated = res.data.content;
                if (updated) {
                    this.form1.patchValue(updated);
                    this.messageService.add({ severity: 'success', summary: 'Reverted', detail: 'Content reverted to selected revision.', life: 3000 });
                }
                this.revisions = [];
                this.showRevisions = false;
            },
            (err: any) => { this.alertService.error(err); },
            () => { this.revisionsLoading = false; }
        );
    }

    // Feature #12: Entity Picker
    openEntityPicker(editor?: any): void {
        if (editor) this.tinyMceEditorRef = editor;
        this.entityPickerSearch = '';
        this.entityPickerTabIndex = 0;
        this.showEntityPicker = true;
        if (this.entityPickerGuides.length === 0 && this.entityPickerFaqs.length === 0) {
            this.loadEntityPickerContents();
        }
    }

    loadEntityPickerContents(): void {
        this.entityPickerLoading = true;
        this.contentService.getByContentType('GUIDE').pipe(first()).subscribe(
            (res: any) => { this.entityPickerGuides = res.data.contents || []; },
            () => {},
            () => { this.entityPickerLoading = false; }
        );
        this.contentService.getByContentType('FAQ').pipe(first()).subscribe(
            (res: any) => { this.entityPickerFaqs = res.data.contents || []; },
            () => {}
        );
    }

    getFilteredEntityItems(): any[] {
        const list = this.entityPickerTabIndex === 0 ? this.entityPickerGuides : this.entityPickerFaqs;
        if (!this.entityPickerSearch) return list;
        const q = this.entityPickerSearch.toLowerCase();
        return list.filter((item: any) => (item.title || '').toLowerCase().includes(q));
    }

    insertEntityLink(item: any): void {
        const prefix = this.entityPickerTabIndex === 0 ? '/guides' : '/faqs';
        const html = `<a href="${prefix}/${item.slug}">${item.title}</a>`;
        if (this.tinyMceEditorRef) {
            this.tinyMceEditorRef.insertContent(html);
        }
        this.showEntityPicker = false;
    }

    // Feature #14: Workflow
    initiateWorkflowTransition(toStatus: string): void {
        this.workflowTargetStatus = toStatus;
        this.workflowComment = '';
        this.workflowAssignedUserId = null;
        this.showWorkflowComment = true;
    }

    confirmWorkflowTransition(): void {
        if (!this.content?.contentId) return;
        this.workflowLoading = true;
        this.showWorkflowComment = false;
        this.contentService.transitionWorkflow(
            this.content.contentId, this.workflowTargetStatus,
            this.workflowComment, this.workflowAssignedUserId
        ).pipe(first()).subscribe(
            (res: any) => {
                const updated = res.data.content;
                if (updated) {
                    this.content = updated;
                    this.form1.patchValue({ status: updated.status });
                }
                this.messageService.add({ severity: 'success', summary: 'Workflow Updated', detail: `Status changed to ${this.workflowTargetStatus}.`, life: 3000 });
            },
            (err: any) => { this.alertService.error(err); },
            () => { this.workflowLoading = false; }
        );
    }

    // Feature #17: Autolink Suggestions
    loadAutolinkSuggestions(): void {
        if (!this.content?.contentId) return;
        this.autolinkLoading = true;
        this.contentService.getAutolinkSuggestions(this.content.contentId).pipe(first()).subscribe(
            (res: any) => {
                this.autolinkSuggestions = res.data.suggestions || [];
                if (this.autolinkSuggestions.length > 0) this.showAutolinkPanel = true;
            },
            () => {},
            () => { this.autolinkLoading = false; }
        );
    }

    applyAutolinkSuggestion(suggestion: any): void {
        this.contentService.applyAutolinkSuggestion(suggestion.suggestionId).pipe(first()).subscribe(
            (res: any) => {
                const updated = res.data.content;
                if (updated?.bodyHtml) {
                    this.form1.patchValue({ bodyHtml: updated.bodyHtml });
                }
                this.autolinkSuggestions = this.autolinkSuggestions.filter(s => s.suggestionId !== suggestion.suggestionId);
                this.messageService.add({ severity: 'success', summary: 'Link Applied', detail: `"${suggestion.matchText}" linked.`, life: 3000 });
            },
            (err: any) => { this.alertService.error(err); }
        );
    }

    dismissAutolinkSuggestion(suggestion: any): void {
        this.contentService.dismissAutolinkSuggestion(suggestion.suggestionId).pipe(first()).subscribe(
            () => {
                this.autolinkSuggestions = this.autolinkSuggestions.filter(s => s.suggestionId !== suggestion.suggestionId);
            },
            (err: any) => { this.alertService.error(err); }
        );
    }

    autoSlug() {
        const title = this.form1.get('title')?.value;
        if (title) {
            const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            this.form1.patchValue({ slug: slug });
        }
    }

    publishNow(): void {
        this.form1.patchValue({ status: 'PUBLISHED' });
        this.formUpdate();
    }
}
